import React, {useState} from 'react';
import {
    getProductItemImage,
    getProductItemImageLabel,
    getProductUrl,
    Product,
} from '@/_application/modules/products/model/product';
import {Image} from '@/_application/core/components/ui/image';
import {productPlaceholder} from '@/lib/placeholders';
import {useTranslation} from 'next-i18next';
import Link from '@/_application/core/components/ui/link';
import {ImageProps} from 'next/image';

type PositionType =
    | 'catalog-grid'
    | 'mini-search'
    | 'home-horizontal'
    | 'home-vertical'
    | 'mini-cart'
    | 'cart-page'
    | 'configurable';

interface ProductImageProps {
    product: Product;
    position?: PositionType;
    imageProps?: Partial<ImageProps>;
}

export const ProductImage = ({product, position, imageProps}: ProductImageProps) => {
    const productUrl = getProductUrl(product, false);
    const {id, sku} = product;

    const webviewData = {
        screen: 'pdp',
        params: {
            sku: sku,
            id: id,
        },
    };
    const webViewNavigation = !!(typeof window !== 'undefined' && window.ReactNativeWebView);
    return (
        <Link href={productUrl} webViewNavigation={webViewNavigation} webViewData={JSON.stringify(webviewData)}>
            <ImageVariant product={product} position={position} imageProps={imageProps} />
        </Link>
    );
};

const ImageVariant = ({product, position, imageProps}: ProductImageProps) => {
    const {t} = useTranslation('common');
    const [imgSrc, setImgSrc] = useState(getProductItemImage(product));
    const currentPosition: PositionType = position || 'catalog-grid';

    switch (currentPosition) {
        case 'mini-search':
            return (
                <Image
                    {...imageProps}
                    src={imgSrc}
                    alt={getProductItemImageLabel(product)}
                    className="product-image h-full w-full"
                    width={120}
                    height={120}
                    onError={() => {
                        setImgSrc(productPlaceholder);
                    }}
                />
            );
        case 'cart-page':
            return (
                <Image
                    {...imageProps}
                    src={imgSrc}
                    alt={getProductItemImageLabel(product)}
                    width={0}
                    height={0}
                    sizes="100vw"
                    className="h-full w-full"
                    onError={() => {
                        setImgSrc(productPlaceholder);
                    }}
                />
            );
        case 'home-horizontal':
            return (
                <Image
                    {...imageProps}
                    src={imgSrc}
                    alt={getProductItemImageLabel(product)}
                    className="product-image"
                    width={150}
                    height={230}
                    onError={() => {
                        setImgSrc(productPlaceholder);
                    }}
                />
            );
        case 'mini-cart':
            return (
                <Image
                    {...imageProps}
                    src={imgSrc}
                    alt={getProductItemImageLabel(product)}
                    className="product-image"
                    width={72}
                    height={72}
                    onError={() => {
                        setImgSrc(productPlaceholder);
                    }}
                />
            );
        case 'configurable': {
            return (
                <div className="relative flex h-[160px] w-full items-center justify-center">
                    {<span className="sr-only">{t('text-product-image')}</span>}
                    <Image
                        {...imageProps}
                        src={imgSrc}
                        alt={getProductItemImageLabel(product)}
                        width={148}
                        height={148}
                        sizes="148px"
                        className="product-image object-contain"
                        onError={() => {
                            setImgSrc(productPlaceholder);
                        }}
                    />
                </div>
            );
        }
        default:
            return (
                <div className="relative flex h-[160px] w-full items-center justify-center">
                    {<span className="sr-only">{t('text-product-image')}</span>}
                    <Image
                        {...imageProps}
                        src={imgSrc}
                        alt={getProductItemImageLabel(product)}
                        width={148}
                        height={148}
                        sizes="148px"
                        quality={75}
                        className="product-image object-contain"
                        onError={() => {
                            setImgSrc(productPlaceholder);
                        }}
                    />
                </div>
            );
    }
};
